import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Brain, Scale, Cpu, CheckCircle } from 'lucide-react';
const pillars = [{
  icon: Brain,
  title: 'Criterio',
  description: 'Pensamiento crítico que ningún algoritmo puede replicar. La capacidad de evaluar, decidir y liderar en entornos de alta incertidumbre.',
  color: '#0EA5E9'
}, {
  icon: Scale,
  title: 'Ética',
  description: 'Valores y carácter insustituibles en la era de las máquinas. El diferenciador humano definitivo que ninguna IA puede programar.',
  color: '#818CF8'
}, {
  icon: Cpu,
  title: 'Pensamiento Algorítmico',
  description: 'La habilidad de diseñar sistemas, no solo usarlos. Ser el arquitecto del futuro, no el usuario de las herramientas de otro.',
  color: '#38BDF8'
}];
export function SolutionSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="py-24 px-4" style={{
    background: 'linear-gradient(180deg, #050A14 0%, #080F20 50%, #050A14 100%)'
  }} data-id="element-187">
      <div className="max-w-5xl mx-auto text-center" data-id="element-188">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-xs font-black tracking-widest uppercase mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{
        color: '#0EA5E9',
        backgroundColor: 'rgba(14,165,233,0.08)',
        border: '1px solid rgba(14,165,233,0.2)'
      }} data-id="element-189">
          EL MÉTODO L-CODE
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="text-4xl md:text-5xl font-black mb-6 leading-tight" style={{
        background: 'linear-gradient(135deg, #F1F5F9 20%, #38BDF8 60%, #818CF8 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-190">
          Presentamos: Arquitectos del Futuro
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-xl font-bold mb-4 max-w-3xl mx-auto" style={{
        color: '#F1F5F9'
      }} data-id="element-191">
          No es un curso de computación. Es un{' '}
          <span style={{
          color: '#F59E0B'
        }} data-id="element-192">
            manifiesto estratégico
          </span>{' '}
          para padres de alto rendimiento.
        </motion.p>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.25
      }} className="text-lg mb-6 max-w-2xl mx-auto leading-relaxed" style={{
        color: '#94A3B8'
      }} data-id="element-193">
          Con el sistema{' '}
          <strong style={{
          color: '#F1F5F9'
        }} data-id="element-194">
            L-Code (Leadership Code)
          </strong>
          , aprenderás a guiar a tu hijo para que vea la IA no como un juguete o
          una trampa, sino como su{' '}
          <span style={{
          color: '#0EA5E9',
          fontWeight: 700
        }} data-id="element-195">
            becario personal.
          </span>
        </motion.p>

        {/* Checklist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="flex flex-col sm:flex-row gap-4 justify-center mb-16" data-id="element-196">
          {['Convierte el tiempo de pantalla en entrenamiento de liderazgo', 'Enseña a tu hijo a ser el arquitecto, no el usuario', 'Desarrolla habilidades que ninguna máquina puede replicar'].map((item, i) => <div key={i} className="flex items-center gap-2 text-sm" style={{
          color: '#94A3B8'
        }} data-id="element-197">
              <CheckCircle size={16} style={{
            color: '#10B981',
            flexShrink: 0
          }} data-id="element-198" />
              {item}
            </div>)}
        </motion.div>

        {/* Pillars */}
        <div className="grid md:grid-cols-3 gap-6" data-id="element-199">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} initial={{
            opacity: 0,
            y: 40
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.65,
            delay: 0.35 + i * 0.12
          }} whileHover={{
            scale: 1.03,
            y: -5
          }} className="p-8 rounded-2xl text-left" style={{
            backgroundColor: '#0C1628',
            border: `1px solid ${pillar.color}20`,
            borderTop: `3px solid ${pillar.color}`
          }} data-id="element-200">
                <div className="w-13 h-13 w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{
              backgroundColor: `${pillar.color}12`,
              border: `1px solid ${pillar.color}30`
            }} data-id="element-201">
                  <Icon size={26} style={{
                color: pillar.color
              }} data-id="element-202" />
                </div>
                <h3 className="text-xl font-black mb-3" style={{
              color: '#F1F5F9'
            }} data-id="element-203">
                  {pillar.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{
              color: '#94A3B8'
            }} data-id="element-204">
                  {pillar.description}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}